import React, { useEffect, useState } from 'react'
import VerdictSeal, { VerdictTone } from './VerdictSeal'

interface DemoClaim {
  claim: string
  verdict: string
  tone: VerdictTone
  confidence: number
  answer: string
  sources: { name: string; cred: number }[]
}

// Mimics real product output: verdict (support-for-claim scale) + confidence
// (how clearly the claim was resolved — high even when the claim is unsupported).
const DEMO_CLAIMS: DemoClaim[] = [
  {
    claim: 'The Great Wall of China is visible from space',
    verdict: 'Limited support',
    tone: 'unverified',
    confidence: 93,
    answer: 'Not visible to the naked eye from low Earth orbit — astronauts have confirmed it repeatedly.',
    sources: [
      { name: 'NASA', cred: 98 },
      { name: 'Scientific American', cred: 92 },
    ],
  },
  {
    claim: 'Honey never spoils',
    verdict: 'Strong support',
    tone: 'true',
    confidence: 94,
    answer: 'Sealed honey is essentially eternal — pots from Egyptian tombs were still edible after 3,000 years.',
    sources: [
      { name: 'Smithsonian', cred: 95 },
      { name: 'Nat Geo', cred: 93 },
    ],
  },
  {
    claim: 'Humans only use 10% of their brains',
    verdict: 'Limited support',
    tone: 'unverified',
    confidence: 96,
    answer: 'Neuroimaging shows virtually every region of the brain is active over the course of a day.',
    sources: [
      { name: 'Mayo Clinic', cred: 97 },
      { name: 'Nature', cred: 96 },
    ],
  },
]

const TONE_TEXT_DARK: Record<VerdictTone, string> = {
  true: 'text-verdict-true-300',
  mixed: 'text-verdict-mixed-300',
  unverified: 'text-verdict-unverified-300',
  false: 'text-verdict-false-300',
}

type Phase = 'typing' | 'scanning' | 'verdict'

/**
 * Self-running verification demo for the landing hero: a claim types itself
 * in, the Scan sweeps, and a Verdict Seal resolves. The product is the demo.
 */
export const HeroVerifyDemo: React.FC = () => {
  const [index, setIndex] = useState(0)
  const [phase, setPhase] = useState<Phase>('typing')
  const [typed, setTyped] = useState('')

  const item = DEMO_CLAIMS[index % DEMO_CLAIMS.length]!

  useEffect(() => {
    if (phase === 'typing') {
      if (typed.length < item.claim.length) {
        const t = setTimeout(() => setTyped(item.claim.slice(0, typed.length + 1)), 30)
        return () => clearTimeout(t)
      }
      const t = setTimeout(() => setPhase('scanning'), 400)
      return () => clearTimeout(t)
    }
    if (phase === 'scanning') {
      const t = setTimeout(() => setPhase('verdict'), 1600)
      return () => clearTimeout(t)
    }
    // verdict — hold, then advance to the next claim
    const t = setTimeout(() => {
      setTyped('')
      setPhase('typing')
      setIndex((i) => (i + 1) % DEMO_CLAIMS.length)
    }, 4800)
    return () => clearTimeout(t)
  }, [phase, typed, item.claim])

  return (
    <div className="w-full max-w-[440px] rounded-xl border border-white/10 bg-white/[0.04] backdrop-blur-sm p-5 shadow-overlay" aria-hidden="true">
      {/* Faux input bar */}
      <div className="flex items-center gap-3 rounded-md bg-white/10 border border-white/10 px-4 py-3">
        <span className="text-white text-sm min-h-[1.25rem]">
          {typed}
          {phase === 'typing' && (
            <span className="inline-block w-px h-4 ml-0.5 align-middle bg-accent-300" style={{ animation: 'tr-caret 1s step-end infinite' }} />
          )}
        </span>
        <span className={`ml-auto text-sm font-semibold ${phase === 'typing' ? 'text-white/40' : 'text-white'}`}>
          Verify
        </span>
      </div>

      {/* Result zone — fixed height so the card doesn't jump between phases */}
      <div className="mt-5 min-h-[190px]">
        {phase === 'scanning' && (
          <div className="pt-8">
            <div className="h-1 rounded-full bg-white/10 overflow-hidden">
              <div
                className="h-full w-1/4 rounded-full bg-accent-300"
                style={{ animation: 'tr-scan 1.1s cubic-bezier(0.4, 0, 0.2, 1) infinite' }}
              />
            </div>
            <p className="mt-4 text-white/60 text-sm">Cross-referencing trusted sources…</p>
          </div>
        )}

        {phase === 'verdict' && (
          <div style={{ animation: 'tr-fade-up 400ms ease-out both' }}>
            <div className="flex items-center gap-4">
              <VerdictSeal confidence={item.confidence} tone={item.tone} size={64} onDark />
              <div>
                <div className={`font-display text-xl font-semibold ${TONE_TEXT_DARK[item.tone]}`}>
                  {item.verdict}
                </div>
                <div className="text-white/50 text-xs mt-0.5 tracking-wide uppercase font-display">
                  {item.verdict} of the claim · {item.confidence}% confidence in our analysis
                </div>
              </div>
            </div>
            <p className="mt-4 text-white/75 text-sm leading-relaxed">{item.answer}</p>
            <div className="mt-4 flex flex-wrap gap-2">
              {item.sources.map((s) => (
                <span key={s.name} className="inline-flex items-center gap-1.5 rounded-full bg-white/10 border border-white/10 px-3 py-1 text-xs text-white/70">
                  <span className={`w-1.5 h-1.5 rounded-full ${item.tone === 'true' ? 'bg-verdict-true-300' : 'bg-verdict-unverified-300'}`} />
                  {s.name} · {s.cred}
                </span>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  )
}

export default HeroVerifyDemo
